import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  getKnowledgeSharing,
  searchKnowledge,
  setKnowledgeSharing,
  type SearchHit
} from '../api/knowledge';
import type {
  KnowledgePageDto,
  KnowledgeSharingDto,
  KnowledgeVisibility,
  ParaCategory
} from '../api/types';
import { useWorkspace } from '../workspace/WorkspaceContext';
import { MarkdownEditor } from '../components/MarkdownEditor';
import { IconChevron } from '../components/icons';
import {
  useArchivePage,
  useCreatePage,
  useCreateThought,
  useDeletePage,
  useDeleteThought,
  useKnowledgePage,
  useKnowledgeTree,
  useMovePage,
  usePageVersions,
  useRevertPage,
  useThoughtSearch,
  useThoughts,
  useUpdatePage as useUpdatePageBase
} from '../hooks/knowledge';
import { formatDate, formatDateTime } from '../utils/dates';
import '../styles/knowledge.scss';

const CATEGORIES: { key: ParaCategory; icon: string }[] = [
  { key: 'PROJECT', icon: '🎯' },
  { key: 'AREA', icon: '🧭' },
  { key: 'RESOURCE', icon: '📚' },
  { key: 'ARCHIVE', icon: '🗃️' }
];

export function KnowledgePage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'knowledge' | 'thoughts'>('knowledge');

  return (
    <section className="kn">
      <header className="kn-head">
        <div className="kn-tabs">
          <button
            className={`kn-tab${tab === 'knowledge' ? ' on' : ''}`}
            onClick={() => setTab('knowledge')}
          >
            {t('knowledge.tabKnowledge')}
          </button>
          <button
            className={`kn-tab${tab === 'thoughts' ? ' on' : ''}`}
            onClick={() => setTab('thoughts')}
          >
            {t('knowledge.tabThoughts')}
          </button>
        </div>
      </header>
      {tab === 'knowledge' ? <KnowledgeBrowser /> : <ThoughtsBrowser />}
    </section>
  );
}

// --- knowledge (PARA tree + editor) -------------------------------------

const EXPAND_STORE = 'msb.kn.expanded.v1';
const WIDTH_STORE = 'msb.kn.treeWidth.v1';
const TREE_MIN = 180;
const TREE_MAX = 720;

function loadExpanded(): Set<string> {
  try {
    const raw = localStorage.getItem(EXPAND_STORE);
    if (raw) return new Set(JSON.parse(raw) as string[]);
  } catch {
    /* corrupt / unavailable storage — fall through to default */
  }
  return new Set(['PROJECT', 'AREA', 'RESOURCE', 'ARCHIVE']); // categories open by default
}

function loadTreeWidth(): number {
  const raw = Number(localStorage.getItem(WIDTH_STORE));
  return raw >= TREE_MIN && raw <= TREE_MAX ? raw : 288;
}

/** True when `candidateId` sits anywhere under `ancestorId` — blocks self-drops. */
function isUnder(pages: KnowledgePageDto[], candidateId: string, ancestorId: string): boolean {
  const parentOf = new Map(pages.map((p) => [p.id, p.parentId ?? null]));
  let cursor: string | null | undefined = candidateId;
  while (cursor) {
    if (cursor === ancestorId) return true;
    cursor = parentOf.get(cursor) ?? null;
  }
  return false;
}

function KnowledgeBrowser() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // selectedId lives in the URL (/app/knowledge/:pageId) so pages are deep-linkable,
  // browser back/forward works, and a refresh restores the open page.
  const { pageId } = useParams<{ pageId: string }>();
  const selectedId = pageId ?? null;
  const tree = useKnowledgeTree();
  const createPage = useCreatePage();
  const movePage = useMovePage();
  const [expanded, setExpanded] = useState<Set<string>>(loadExpanded);
  const [treeWidth, setTreeWidth] = useState<number>(loadTreeWidth);
  // On narrow screens the tree is an off-canvas drawer; on desktop this flag is
  // inert (CSS keeps the tree always visible).
  const [treeOpen, setTreeOpen] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const searchActive = searchQ.trim().length >= 2;
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);

  const pages = tree.data ?? [];

  useEffect(() => {
    try {
      localStorage.setItem(EXPAND_STORE, JSON.stringify([...expanded]));
    } catch {
      /* storage full / unavailable — non-fatal */
    }
  }, [expanded]);

  useEffect(() => {
    try {
      localStorage.setItem(WIDTH_STORE, String(treeWidth));
    } catch {
      /* storage full / unavailable — non-fatal */
    }
  }, [treeWidth]);

  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = treeWidth;
    const onMove = (ev: MouseEvent) =>
      setTreeWidth(Math.min(TREE_MAX, Math.max(TREE_MIN, startWidth + ev.clientX - startX)));
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.classList.remove('kn-resizing');
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    document.body.classList.add('kn-resizing');
  };

  /** A node may not be dropped on itself or anywhere inside its own subtree. */
  const canDropOn = (targetId: string) =>
    Boolean(dragId) && !isUnder(pages, targetId, dragId as string);

  const dropOnNode = (targetId: string) => {
    if (dragId && canDropOn(targetId)) {
      movePage.mutate({ id: dragId, parentId: targetId });
      setExpanded((prev) => new Set(prev).add(targetId));
    }
    setDragId(null);
    setDropTarget(null);
  };

  const dropOnCategory = (category: ParaCategory) => {
    if (dragId) movePage.mutate({ id: dragId, parentId: null, paraCategory: category });
    setDragId(null);
    setDropTarget(null);
  };

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const select = (id: string) => {
    navigate(`/app/knowledge/${id}`); // history push — back/forward + refresh keep the view
    setExpanded((prev) => new Set(prev).add(id)); // opening a page reveals its children
    setTreeOpen(false); // mobile: picking a page closes the tree drawer to reveal content
  };

  // Deep-link / refresh: once the tree is loaded, expand every ancestor (and the
  // category) of the URL-selected page so it's visible in the tree.
  useEffect(() => {
    if (!selectedId || pages.length === 0) return;
    const byId = new Map(pages.map((p) => [p.id, p]));
    const target = byId.get(selectedId);
    if (!target) return;
    const reveal: string[] = [target.paraCategory];
    let cursor = target.parentId ?? null;
    while (cursor) {
      reveal.push(cursor);
      cursor = byId.get(cursor)?.parentId ?? null;
    }
    setExpanded((prev) => {
      if (reveal.every((id) => prev.has(id))) return prev; // nothing new — avoid a needless render
      const next = new Set(prev);
      reveal.forEach((id) => next.add(id));
      return next;
    });
  }, [selectedId, tree.data]);

  const addPage = (category: ParaCategory, parentId: string | null) => {
    if (createPage.isPending) return;
    createPage.mutate(
      { paraCategory: category, parentId, title: t('knowledge.newPageDefault') },
      {
        onSuccess: (dto) => {
          setExpanded((prev) => new Set(prev).add(parentId ?? category));
          navigate(`/app/knowledge/${dto.id}`);
        }
      }
    );
  };

  return (
    <div className={`kn-body${treeOpen ? ' tree-open' : ''}`}>
      <button
        type="button"
        className="kn-tree-fab"
        onClick={() => setTreeOpen((v) => !v)}
        aria-label={t('knowledge.toggleTree')}
      >
        <IconChevron className="kn-tree-fab-icon" />
        {t('knowledge.tree')}
      </button>
      <div
        className="kn-tree-backdrop"
        onClick={() => setTreeOpen(false)}
        aria-hidden="true"
      />
      <aside className="kn-tree" style={{ width: treeWidth }}>
        <input
          className="kn-tree-search"
          type="search"
          value={searchQ}
          placeholder={t('knowledge.searchPlaceholder') ?? ''}
          onChange={(e) => setSearchQ(e.target.value)}
        />
        {searchActive && (
          <KnowledgeSearchResults
            q={searchQ.trim()}
            onOpen={(id) => {
              select(id);
              setSearchQ('');
            }}
          />
        )}
        {!searchActive &&
          CATEGORIES.map(({ key, icon }) => {
          const open = expanded.has(key);
          const count = pages.filter((p) => p.paraCategory === key).length;
          return (
            <div key={key} className="kn-cat">
              <div
                className={`kn-cat-head${open ? ' open' : ''}${dropTarget === key ? ' drop' : ''}`}
                onClick={() => toggle(key)}
                onDragOver={(e) => {
                  if (!dragId) return;
                  e.preventDefault();
                  setDropTarget(key);
                }}
                onDragLeave={() => setDropTarget((prev) => (prev === key ? null : prev))}
                onDrop={(e) => {
                  e.preventDefault();
                  dropOnCategory(key);
                }}
              >
                <button type="button" className={`kn-twist${open ? ' open' : ''}`} aria-label={t(open ? 'knowledge.collapse' : 'knowledge.expand')}>
                  <IconChevron className="kn-twist-icon" />
                </button>
                <span className="kn-cat-icon">{icon}</span>
                <span className="kn-cat-name">{t(`knowledge.para.${key}`)}</span>
                {count > 0 && <span className="kn-cat-count">{count}</span>}
                <button
                  type="button"
                  className="kn-add"
                  title={t('knowledge.addTop')}
                  onClick={(e) => {
                    e.stopPropagation();
                    addPage(key, null);
                  }}
                >
                  +
                </button>
              </div>
              {open && (
                <TreeLevel
                  pages={pages}
                  category={key}
                  parentId={null}
                  depth={0}
                  selectedId={selectedId}
                  expanded={expanded}
                  onSelect={select}
                  onToggle={toggle}
                  onAddChild={(pid) => addPage(key, pid)}
                  dragId={dragId}
                  dropTarget={dropTarget}
                  canDropOn={canDropOn}
                  onDragStartNode={setDragId}
                  onDragEndNode={() => {
                    setDragId(null);
                    setDropTarget(null);
                  }}
                  onDragOverNode={setDropTarget}
                  onDropNode={dropOnNode}
                />
              )}
            </div>
          );
        })}
      </aside>

      <div
        className="kn-splitter"
        role="separator"
        aria-orientation="vertical"
        title={t('knowledge.resizeTree')}
        onMouseDown={startResize}
      />

      <div className="kn-editor-pane">
        {selectedId ? (
          <PageEditor
            key={selectedId}
            pageId={selectedId}
            pages={pages}
            onOpen={select}
            onDeleted={() => navigate('/app/knowledge')}
          />
        ) : (
          <div className="kn-empty">{t('knowledge.emptyEditor')}</div>
        )}
      </div>
    </div>
  );
}

function KnowledgeSearchResults({
  q,
  onOpen
}: {
  q: string;
  onOpen: (pageId: string) => void;
}) {
  const { t } = useTranslation();
  const { data, isFetching } = useQuery({
    queryKey: ['kn-search', q],
    queryFn: () => searchKnowledge(q),
    enabled: q.length >= 2,
    staleTime: 30_000
  });
  const hits = (data ?? []).filter((h: SearchHit) => h.pageId);

  return (
    <div className="kn-search-results">
      {isFetching && hits.length === 0 && <div className="kn-search-hint">{t('app.loading')}</div>}
      {!isFetching && hits.length === 0 && (
        <div className="kn-search-hint">{t('knowledge.searchNone')}</div>
      )}
      {hits.map((h) => (
        <button
          key={h.id + (h.heading ?? '')}
          type="button"
          className="kn-search-hit"
          onClick={() => onOpen(h.pageId as string)}
        >
          <span className="kn-search-hit-title">{h.title ?? '—'}</span>
          {h.branch && h.branch !== h.title && (
            <span className="kn-search-hit-branch">{h.branch}</span>
          )}
          {h.snippet && <span className="kn-search-hit-snippet">{h.snippet}</span>}
        </button>
      ))}
    </div>
  );
}

interface TreeLevelProps {
  pages: KnowledgePageDto[];
  category: ParaCategory;
  parentId: string | null;
  depth: number;
  selectedId: string | null;
  expanded: Set<string>;
  onSelect: (id: string) => void;
  onToggle: (id: string) => void;
  onAddChild: (parentId: string) => void;
  dragId: string | null;
  dropTarget: string | null;
  canDropOn: (id: string) => boolean;
  onDragStartNode: (id: string) => void;
  onDragEndNode: () => void;
  onDragOverNode: (id: string) => void;
  onDropNode: (id: string) => void;
}

function TreeLevel({
  pages,
  category,
  parentId,
  depth,
  selectedId,
  expanded,
  onSelect,
  onToggle,
  onAddChild,
  dragId,
  dropTarget,
  canDropOn,
  onDragStartNode,
  onDragEndNode,
  onDragOverNode,
  onDropNode
}: TreeLevelProps) {
  const { t } = useTranslation();
  // Only the top level is category-scoped; deeper levels follow parentId alone, so a
  // child whose paraCategory drifted from its parent still shows up under it instead
  // of silently vanishing from the tree.
  const nodes = pages
    .filter((p) =>
      parentId === null
        ? p.paraCategory === category && (p.parentId ?? null) === null
        : p.parentId === parentId
    )
    .sort((a, b) => a.position - b.position || a.title.localeCompare(b.title));

  if (nodes.length === 0) return null;

  return (
    <div className="kn-level">
      {nodes.map((p) => {
        const hasChildren = pages.some((c) => c.parentId === p.id);
        const isOpen = expanded.has(p.id);
        return (
          <div key={p.id} className="kn-branch">
            <div
              className={`kn-node${selectedId === p.id ? ' on' : ''}`
                + (dragId === p.id ? ' dragging' : '')
                + (dropTarget === p.id && canDropOn(p.id) ? ' drop' : '')}
              style={{ paddingLeft: 6 + depth * 15 }}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', p.id);
                onDragStartNode(p.id);
              }}
              onDragEnd={onDragEndNode}
              onDragOver={(e) => {
                if (!dragId || !canDropOn(p.id)) return;
                e.preventDefault();
                e.stopPropagation();
                onDragOverNode(p.id);
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDropNode(p.id);
              }}
            >
              <button
                type="button"
                className={`kn-twist${isOpen ? ' open' : ''}${hasChildren ? '' : ' leaf'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (hasChildren) onToggle(p.id);
                }}
                aria-label={hasChildren ? (isOpen ? t('knowledge.collapse') : t('knowledge.expand')) : ''}
                tabIndex={hasChildren ? 0 : -1}
              >
                {hasChildren && <IconChevron className="kn-twist-icon" />}
              </button>
              <button type="button" className="kn-node-body" onClick={() => onSelect(p.id)}>
                <span className="kn-node-icon">{p.icon || '📄'}</span>
                <span className="kn-node-title">{p.title}</span>
              </button>
              <button
                type="button"
                className="kn-add kn-add-child"
                title={t('knowledge.addSub')}
                onClick={(e) => {
                  e.stopPropagation();
                  onAddChild(p.id);
                }}
              >
                +
              </button>
            </div>
            {hasChildren && isOpen && (
              <TreeLevel
                pages={pages}
                category={category}
                parentId={p.id}
                depth={depth + 1}
                selectedId={selectedId}
                expanded={expanded}
                onSelect={onSelect}
                onToggle={onToggle}
                onAddChild={onAddChild}
                dragId={dragId}
                dropTarget={dropTarget}
                canDropOn={canDropOn}
                onDragStartNode={onDragStartNode}
                onDragEndNode={onDragEndNode}
                onDragOverNode={onDragOverNode}
                onDropNode={onDropNode}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function PageEditor({
  pageId,
  pages,
  onOpen,
  onDeleted
}: {
  pageId: string;
  pages: KnowledgePageDto[];
  onOpen: (id: string) => void;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const pageQuery = useKnowledgePage(pageId);
  const updatePage = useUpdatePageDebounced(pageId);
  const deletePage = useDeletePage();
  const archivePage = useArchivePage();
  const titleRef = useRef<HTMLTextAreaElement | null>(null);
  const children = pages
    .filter((p) => p.parentId === pageId)
    .sort((a, b) => a.position - b.position || a.title.localeCompare(b.title));
  const [showHistory, setShowHistory] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [title, setTitle] = useState('');
  const [icon, setIcon] = useState<string | null>(null);
  const [contentOpen, setContentOpen] = useState(false);
  const initialEmpty = useRef(false);
  const page = pageQuery.data;

  useEffect(() => {
    if (page) {
      setTitle(page.title);
      setIcon(page.icon);
      initialEmpty.current = !(page.bodyMarkdown ?? '').trim();
    }
  }, [page?.id]);

  // The title is a textarea so long titles wrap instead of being clipped; keep its
  // height glued to the content.
  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [title]);

  const pickIcon = (next: string | null) => {
    setIcon(next);
    setShowEmoji(false);
    updatePage.save({ icon: next });
  };

  if (!page) return <div className="kn-empty">{t('app.loading')}</div>;

  const bodyEmpty = !(page.bodyMarkdown ?? '').trim();
  const showEditor = !bodyEmpty || contentOpen;
  const subpagesFirst = initialEmpty.current && children.length > 0;
  const subpagesBlock =
    children.length > 0 ? (
      <div className={`kn-subpages${subpagesFirst ? ' kn-subpages-top' : ''}`}>
        <div className="kn-subpages-head">{t('knowledge.subpages')}</div>
        {children.map((c) => (
          <button key={c.id} type="button" className="kn-subpage" onClick={() => onOpen(c.id)}>
            <span className="kn-subpage-icon">{c.icon || '📄'}</span>
            <span className="kn-subpage-title">{c.title}</span>
            <IconChevron className="kn-subpage-arrow" />
          </button>
        ))}
      </div>
    ) : null;

  return (
    <div className="kn-doc">
      <div className="kn-doc-bar">
        <span className="kn-crumb">{t(`knowledge.para.${page.paraCategory}`)}</span>
        <span className="spacer" />
        <span className="kn-saved">{updatePage.saving ? t('knowledge.saving') : t('knowledge.saved')}</span>
        {page.visibility && page.visibility !== 'PRIVATE' && (
          <span className={`kn-vis-badge kn-vis-${page.visibility.toLowerCase()}`}>
            {t(`knowledge.vis.${page.visibility}`)}
          </span>
        )}
        <button className="kn-ghost" onClick={() => setShowShare(true)}>
          {t('knowledge.share')}
        </button>
        <button className="kn-ghost" onClick={() => setShowHistory((v) => !v)}>
          {t('knowledge.history')}
        </button>
        <button
          className="kn-ghost"
          title={t('knowledge.archiveHint') ?? ''}
          disabled={page.paraCategory === 'ARCHIVE' || archivePage.isPending}
          onClick={() => archivePage.mutate(pageId)}
        >
          {t('knowledge.archive')}
        </button>
        <button
          className="kn-ghost kn-danger"
          onBlur={() => setConfirmDelete(false)}
          onClick={() => {
            if (!confirmDelete) {
              setConfirmDelete(true);
              return;
            }
            deletePage.mutate(pageId, { onSuccess: onDeleted });
          }}
        >
          {confirmDelete ? t('knowledge.deleteSure') : t('knowledge.delete')}
        </button>
      </div>

      <div className="kn-doc-heading">
        <div className="kn-emoji-wrap">
          <button
            type="button"
            className="kn-doc-emoji"
            onClick={() => setShowEmoji((v) => !v)}
            title={t('knowledge.setIcon')}
          >
            {icon || '📄'}
          </button>
          {showEmoji && (
            <EmojiPicker current={icon} onPick={pickIcon} onClose={() => setShowEmoji(false)} />
          )}
        </div>
        <textarea
          ref={titleRef}
          className="kn-doc-title"
          rows={1}
          value={title}
          onChange={(e) => setTitle(e.target.value.replace(/\n/g, ''))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.currentTarget.blur();
            }
          }}
          onBlur={() => {
            const trimmed = title.trim();
            if (trimmed && trimmed !== page.title) updatePage.save({ title: trimmed });
          }}
          placeholder={t('knowledge.titlePlaceholder') ?? ''}
        />
      </div>

      {subpagesFirst && subpagesBlock}

      {showEditor ? (
        <MarkdownEditor
          value={page.bodyMarkdown ?? ''}
          autoFocus={contentOpen}
          onChange={(md) => updatePage.save({ bodyMarkdown: md })}
          onOpenInternal={onOpen}
        />
      ) : (
        <button type="button" className="kn-add-content" onClick={() => setContentOpen(true)}>
          <span className="kn-add-content-plus">+</span>
          {t('knowledge.addContent')}
        </button>
      )}

      {!subpagesFirst && subpagesBlock}

      {showHistory && <HistoryPanel pageId={pageId} onClose={() => setShowHistory(false)} />}
      {showShare && (
        <ShareDialog pageId={pageId} pageTitle={page.title} onClose={() => setShowShare(false)} />
      )}
    </div>
  );
}

function ShareDialog({
  pageId,
  pageTitle,
  onClose
}: {
  pageId: string;
  pageTitle: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { members } = useWorkspace();
  const [visibility, setVisibility] = useState<KnowledgeVisibility>('PRIVATE');
  const [subtree, setSubtree] = useState(true);
  const [userIds, setUserIds] = useState<string[]>([]);
  const [email, setEmail] = useState('');
  const [emails, setEmails] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [unknown, setUnknown] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  const [notifyByEmail, setNotifyByEmail] = useState(true);
  const [saved, setSaved] = useState(false);
  const [sharedMembers, setSharedMembers] = useState<
    { userId: string; email: string; displayName: string }[]
  >([]);

  const publicUrl = `${window.location.origin}/p/${pageId}`;

  useEffect(() => {
    let alive = true;
    getKnowledgeSharing(pageId)
      .then((s) => {
        if (!alive) return;
        setVisibility(s.visibility);
        setSubtree(s.shareScope !== 'PAGE');
        setUserIds(s.sharedWith.map((m) => m.userId));
        setSharedMembers(s.sharedWith);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [pageId]);

  // The people to show as checkboxes: family members PLUS anyone the page is
  // already shared with (e.g. added by email, not in the family) — deduped, so a
  // current share is always visible and removable, not just the family roster.
  const roster = useMemo(() => {
    const map = new Map<string, { id: string; displayName: string; email: string }>();
    members.forEach((m) => map.set(m.id, { id: m.id, displayName: m.displayName, email: m.email }));
    sharedMembers.forEach((m) =>
      map.set(m.userId, { id: m.userId, displayName: m.displayName, email: m.email })
    );
    return [...map.values()];
  }, [members, sharedMembers]);

  const toggleMember = (id: string) =>
    setUserIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const addEmail = () => {
    const e = email.trim().toLowerCase();
    if (e && !emails.includes(e)) setEmails((prev) => [...prev, e]);
    setEmail('');
  };

  const save = async (apply: KnowledgeVisibility) => {
    setSaving(true);
    setUnknown([]);
    try {
      const res: KnowledgeSharingDto = await setKnowledgeSharing(pageId, {
        visibility: apply,
        shareScope: subtree ? 'SUBTREE' : 'PAGE',
        userIds: apply === 'SHARED' ? userIds : [],
        emails: apply === 'SHARED' ? emails : [],
        notifyByEmail
      });
      setVisibility(res.visibility);
      setUserIds(res.sharedWith.map((m) => m.userId));
      setSharedMembers(res.sharedWith);
      setEmails([]);
      setUnknown(res.unknownEmails);
      setSaved(true); // confirmation stays until the user closes the dialog
    } finally {
      setSaving(false);
    }
  };

  const copyLink = () => {
    void navigator.clipboard?.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <>
      <div className="kn-share-backdrop" onClick={onClose} />
      <div className="kn-share" role="dialog" aria-label={t('knowledge.share')}>
        <div className="kn-share-head">
          <span className="kn-share-title">{t('knowledge.shareTitle', { title: pageTitle })}</span>
          <button className="kn-ghost" onClick={onClose}>
            ×
          </button>
        </div>

        {loading ? (
          <div className="kn-share-loading">{t('app.loading')}</div>
        ) : (
          <>
            <div className="kn-share-levels">
              {(['PRIVATE', 'SHARED', 'PUBLIC'] as KnowledgeVisibility[]).map((v) => (
                <button
                  key={v}
                  type="button"
                  className={`kn-share-level${visibility === v ? ' on' : ''}`}
                  onClick={() => {
                    setVisibility(v);
                    setSaved(false);
                  }}
                >
                  <span className="kn-share-level-name">{t(`knowledge.vis.${v}`)}</span>
                  <span className="kn-share-level-desc">{t(`knowledge.visDesc.${v}`)}</span>
                </button>
              ))}
            </div>

            {visibility !== 'PRIVATE' && (
              <label className="kn-share-scope">
                <input
                  type="checkbox"
                  checked={subtree}
                  onChange={(e) => setSubtree(e.target.checked)}
                />
                {t('knowledge.shareSubtree')}
              </label>
            )}

            {visibility === 'PUBLIC' && (
              <div className="kn-share-public">
                <input className="kn-share-link" readOnly value={publicUrl} onFocus={(e) => e.currentTarget.select()} />
                <button className="btn" onClick={copyLink}>
                  {copied ? t('knowledge.copied') : t('knowledge.copyLink')}
                </button>
              </div>
            )}

            {visibility === 'SHARED' && (
              <div className="kn-share-people">
                {roster.length > 0 && <div className="kn-share-sub">{t('knowledge.shareFamily')}</div>}
                {roster.map((m) => (
                  <label key={m.id} className="kn-share-member">
                    <input
                      type="checkbox"
                      checked={userIds.includes(m.id)}
                      onChange={() => toggleMember(m.id)}
                    />
                    <span className="kn-share-member-name">{m.displayName}</span>
                    <span className="kn-share-member-email">{m.email}</span>
                  </label>
                ))}
                <div className="kn-share-sub">{t('knowledge.shareEmail')}</div>
                <div className="kn-share-addemail">
                  <input
                    className="kn-share-email-input"
                    type="email"
                    value={email}
                    placeholder={t('knowledge.shareEmailPlaceholder') ?? ''}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addEmail();
                      }
                    }}
                  />
                  <button className="kn-ghost" onClick={addEmail}>
                    +
                  </button>
                </div>
                {emails.map((e) => (
                  <span key={e} className="kn-share-pill">
                    {e}
                    <button onClick={() => setEmails((prev) => prev.filter((x) => x !== e))}>×</button>
                  </span>
                ))}
                {unknown.length > 0 && (
                  <div className="kn-share-warn">{t('knowledge.shareUnknown', { emails: unknown.join(', ') })}</div>
                )}
                <label className="kn-share-scope kn-share-notify">
                  <input
                    type="checkbox"
                    checked={notifyByEmail}
                    onChange={(e) => setNotifyByEmail(e.target.checked)}
                  />
                  {t('knowledge.shareNotify')}
                </label>
              </div>
            )}

            {saved && <div className="kn-share-ok">✓ {t('knowledge.shareSaved')}</div>}

            <div className="kn-share-actions">
              <button className="kn-ghost" onClick={onClose}>
                {saved ? t('knowledge.done') : t('knowledge.cancel')}
              </button>
              {!saved && (
                <button className="btn btn-primary" disabled={saving} onClick={() => save(visibility)}>
                  {saving ? t('knowledge.saving') : t('knowledge.saveShare')}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

const EMOJI_CHOICES = [
  '📄', '📝', '📌', '📎', '🗂️', '📁', '📚', '📖', '📗', '📘',
  '🎯', '🚀', '💡', '🔑', '⭐', '🔥', '✅', '⚙️', '🧭', '🗺️',
  '🏠', '💰', '💼', '🧾', '📊', '📈', '🧠', '❤️', '🍳', '🥗',
  '🏃', '🏋️', '🌱', '🌍', '✈️', '🎨', '🎵', '📷', '🛠️', '🧪',
  '👨‍👩‍👧‍👦', '🐾', '☕', '🍷', '🎁', '📅', '⏰', '🔒', '💊', '🩺'
];

function EmojiPicker({
  current,
  onPick,
  onClose
}: {
  current: string | null;
  onPick: (emoji: string | null) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <div className="kn-emoji-backdrop" onClick={onClose} />
      <div className="kn-emoji-pop" role="menu">
        <div className="kn-emoji-grid">
          {EMOJI_CHOICES.map((e) => (
            <button
              key={e}
              type="button"
              className={`kn-emoji-cell${current === e ? ' on' : ''}`}
              onClick={() => onPick(e)}
            >
              {e}
            </button>
          ))}
        </div>
        <button type="button" className="kn-emoji-clear" onClick={() => onPick(null)}>
          {t('knowledge.clearIcon')}
        </button>
      </div>
    </>
  );
}

function HistoryPanel({ pageId, onClose }: { pageId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const versions = usePageVersions(pageId);
  const revert = useRevertPage();

  return (
    <div className="kn-history">
      <div className="kn-history-head">
        <span>{t('knowledge.history')}</span>
        <button className="kn-ghost" onClick={onClose}>
          ×
        </button>
      </div>
      <div className="kn-history-list">
        {(versions.data ?? []).map((v) => (
          <div key={v.id} className="kn-version">
            <div className="kn-version-meta">
              <strong>v{v.versionNo}</strong>
              <span className={`kn-badge kn-badge-${v.author}`}>
                {t(`knowledge.author.${v.author}`)}
              </span>
              <time>{formatDateTime(v.createdAt)}</time>
            </div>
            {v.summary && <div className="kn-version-summary">{v.summary}</div>}
            <button
              className="kn-ghost"
              onClick={() => revert.mutate({ id: pageId, versionNo: v.versionNo })}
            >
              {t('knowledge.revert')}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Debounced page save (title/body) with a small "saving" indicator. */
function useUpdatePageDebounced(pageId: string) {
  const mutation = useUpdatePageBase();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);

  const flush = () => {
    const patch = pending.current;
    pending.current = {};
    if (Object.keys(patch).length === 0) return;
    setSaving(true);
    mutation.mutate({ id: pageId, patch }, { onSettled: () => setSaving(false) });
  };

  const save = (patch: Record<string, unknown>) => {
    pending.current = { ...pending.current, ...patch };
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(flush, 700);
  };

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  return { save, saving };
}

// --- thoughts -----------------------------------------------------------

function ThoughtsBrowser() {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const create = useCreateThought();
  const del = useDeleteThought();
  const all = useThoughts();
  const searched = useThoughtSearch(query);
  const list = query.trim() ? searched.data ?? [] : all.data ?? [];

  const submit = () => {
    const body = draft.trim();
    if (!body || create.isPending) return;
    create.mutate({ body }, { onSuccess: () => setDraft('') });
  };

  return (
    <div className="kn-thoughts">
      <div className="kn-thought-compose">
        <textarea
          className="kn-thought-input"
          value={draft}
          placeholder={t('knowledge.thoughtPlaceholder') ?? ''}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submit();
          }}
        />
        <button className="btn btn-primary" disabled={!draft.trim() || create.isPending} onClick={submit}>
          {t('knowledge.saveThought')}
        </button>
      </div>

      <input
        className="kn-search"
        value={query}
        placeholder={t('knowledge.searchThoughts') ?? ''}
        onChange={(e) => setQuery(e.target.value)}
      />

      <div className="kn-thought-list">
        {list.length === 0 && <div className="kn-empty">{t('knowledge.noThoughts')}</div>}
        {list.map((th) => (
          <div key={th.id} className="kn-thought">
            <div className="kn-thought-body">{th.body}</div>
            <div className="kn-thought-foot">
              <time>{formatDate(th.createdAt)}</time>
              {th.curatedAt && <span className="kn-badge kn-badge-curation">{t('knowledge.curated')}</span>}
              <span className="spacer" />
              <button className="kn-ghost" onClick={() => del.mutate(th.id)}>
                {t('knowledge.delete')}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
